import type { ButtonInfo } from './ImportResolutionBadge';

export const RECON_TABLE_INFO: Record<
  | 'reconcilePage' | 'editableQty' | 'syncToPo' | 'syncHow' | 'salesPrice' | 'poStatus'
  | 'poOrderedQty' | 'poReceivedQty' | 'poPurchasePrice' | 'billQty' | 'gstToggle'
  | 'copiedToJobPo' | 'copiedToJobBill' | 'removeLine' | 'billHeader',
  ButtonInfo
> = {
  reconcilePage: {
    title: 'Supplier bill reconciliation',
    description: 'Match a supplier invoice to its purchase order — import the PDF, check each line side-by-side, sync quantities and prices to the PO, then approve for payment.',
    example: 'Drop the Hoseforce invoice on BILL-2026-018, reconcile each line against PO-SV-BLANK, sync, then approve.',
  },
  billHeader: {
    title: 'Bill (imported from supplier invoice)',
    description: 'The right-hand side is this Bill — a FieldInsight record created from the supplier\u2019s invoice. Its lines were imported from the supplier invoice PDF/image you dropped in; you reconcile them against the linked PO on the left.',
    example: 'Drop the Quickensted invoice on BILL-2026-020 and its lines appear here, ready to match against PO-1649.',
  },
  editableQty: {
    title: 'Editable qty columns',
    description: 'PO Qty, Rcvd, Purchase $, and Bill Qty save on blur or Enter. Esc cancels.',
    example: 'Adjust PO received from 0 → 4 when the warehouse confirms partial delivery on PO-1649.',
  },
  syncToPo: {
    title: 'Sync Bill to PO',
    description: 'Compares bill lines to the linked PO and copies matched values. Opens a review dialog when variances exist.',
    example: 'Sync BILL-2026-020 to PO-1649 after Alfred parses the Quickensted invoice.',
  },
  syncHow: {
    title: 'How sync works',
    description: 'Clean matches auto-sync. Mismatches open a line-by-line review with Receipt all and Update ordered qty options.',
    example: 'A 3% price variance on one line opens the dialog; exact-match lines still sync in one click.',
  },
  salesPrice: {
    title: 'Sales $ column',
    description: 'Shows sell price per line using Bill Markup %. Hidden by default to keep the reconcile grid lean.',
    example: 'Turn on Sales $ at 65% markup to sanity-check margin on PO-1649 lines.',
  },
  poStatus: {
    title: 'PO status',
    description: 'Workflow state of the linked purchase order. Changing it here updates the PO without leaving reconcile.',
    example: 'Set PO-1649 to Bill received after syncing the supplier invoice.',
  },
  poOrderedQty: {
    title: 'PO ordered qty',
    description: 'Quantity originally approved on the purchase order line.',
    example: 'Ordered 10 on PO-1649 line 1 — edit if the PO was raised with the wrong qty.',
  },
  poReceivedQty: {
    title: 'PO received qty',
    description: 'Quantity physically received to date on this PO line.',
    example: 'Set received to 4 when only part of the order has arrived.',
  },
  poPurchasePrice: {
    title: 'PO purchase price',
    description: 'Unit cost on the PO line before markup.',
    example: 'Update purchase $ to $6.50 when the supplier invoice shows a price correction.',
  },
  billQty: {
    title: 'Bill qty',
    description: 'Quantity claimed on this supplier invoice line.',
    example: 'Bill shows 12 units but PO ordered 10 — adjust bill qty or accept the variance at sync.',
  },
  gstToggle: {
    title: 'GST treatment',
    description: 'Ex or Inc GST for this line. Click to override the global Ex/Inc toggle for one line.',
    example: 'Toggle a freight line to Inc GST when the supplier invoice includes tax in the unit price.',
  },
  copiedToJobPo: {
    title: 'Copied to job (PO)',
    description: 'This PO line cost has been pushed to the linked job.',
    example: 'Green briefcase on PO-1649 line 2 after Copy to SRF.',
  },
  copiedToJobBill: {
    title: 'Copied to job (Bill)',
    description: 'This bill line cost has been pushed to the linked job.',
    example: 'Green briefcase on BILL-2026-020 line 1 after Copy to Job.',
  },
  removeLine: {
    title: 'Remove line',
    description: 'Deletes this bill line from the reconciliation table.',
    example: 'Remove a duplicate line Alfred parsed twice from the same invoice.',
  },
};
